import { useRef, useState } from 'react';
import { uploadFile } from '../../lib/storage';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';

export default function ImageUploader({ bucket, pathPrefix, currentUrl, onUpload, label = 'Image' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please select a JPG, PNG, or WebP image.');
      return;
    }
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const url = await uploadFile(bucket, pathPrefix, file, setProgress);
      onUpload(url);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-brand-dark">{label}</label>

      {currentUrl && (
        <img
          src={currentUrl}
          alt="Current"
          className="w-full max-h-48 object-cover rounded-lg border border-gray-200"
        />
      )}

      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-brand-purple bg-purple-50' : 'border-gray-300 hover:border-brand-purple'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        {uploading ? (
          <div>
            <p className="text-sm text-gray-600 mb-2">Uploading… {progress}%</p>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-brand-purple transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            Click or drag & drop an image (JPG, PNG, WebP)
          </p>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
